
import { TCompliantBlockRow } from "@/models/compliant_block";
import { IDashboard } from "@/models/dashboards";
import { SMUser, SMValidationModal } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { COMPLIANT, UNDERTERMINED } from "@/constants/compliance";

const HOME_COMPLIANT_TITLE_CLASS = "widget-title";
const COMPLIANCE_BLOCK_CLASS = "cursor-pointer";
const TASKS_PATH = "/tasks/";

@Component({
  components: {
    CompliantBlock: () =>
      import("@/modules/Compliance/ComplianceWidgets/components/CompliantBlock.vue"),
    ComplianceBlock: () =>
      import("@/modules/Compliance/ComplianceWidgets/components/ComplianceBlock.vue"),
    IconBlock: () => import("@/modules/Compliance/Blocks/IconBlock/IconBlock.vue"),
    TasksBlock: () => import("@/modules/Compliance/Blocks/TasksBlock/TasksBlock.vue"),
  },
})
export default class ComplianceWidget extends Vue {
  @Prop({ type: Number, default: 0 }) readonly count!: number;

  @Watch("isValidationModalOpen")
  isValidationModalOpenChanged() {
    if (!this.isValidationModalOpen) {
      this.getCompanyDashboard();
    }
  }

  isCompliant: boolean | null = null;

  dashboard: IDashboard = {
    compliance_status: UNDERTERMINED,
    employee_record_keeping_duties: null,
    employee_reporting_duties: null,
    employer_genuine_employment_duties: null,
    employer_record_keeping_duties: null,
    employer_reporting_duties: null,
  };

  complianceStatus = "";

  statuses = {
    undetermined: this.$root.$tc("labels.undetermined"),
    compliant: this.$root.$tc("labels.compliant"),
    not_compliant: this.$root.$tc("labels.notCompliant"),
  };

  get isValidationModalOpen() {
    return SMValidationModal.isOpen;
  }

  get isComplianceBlocked() {
    return SMUser.user?.is_compliance_blocked;
  }

  get title() {
    if (this.isComplianceBlocked) return this.$root.$tc("labels.undetermined");

    return this.complianceStatus;
  }

  created() {
    this.getCompanyDashboard();
  }

  async getCompanyDashboard() {
    const { data } = await this.axios.get<IDashboard>(API_ROUTES.COMPLIANCE_STATUS);

    this.complianceStatus = this.statuses[data.compliance_status];

    this.dashboard = data;

    this.isCompliant = this.dashboard.compliance_status === COMPLIANT;
  }

  get companyDashboardRows() {
    const {
      employer_record_keeping_duties,
      employer_reporting_duties,
      employer_genuine_employment_duties,
    } = this.dashboard;

    const rows: TCompliantBlockRow[] = [
      { label: this.$tc("labels.recordKeepingDuties"), checked: employer_record_keeping_duties },
      { label: this.$tc("labels.reportingDuties"), checked: employer_reporting_duties },
      {
        label: this.$tc("labels.genuineEmploymentDuties"),
        checked: employer_genuine_employment_duties,
      },
    ];

    return rows;
  }

  get employeesDashboardRows() {
    const { employee_record_keeping_duties, employee_reporting_duties } = this.dashboard;

    const rows: TCompliantBlockRow[] = [
      { label: this.$tc("labels.recordKeepingDuties"), checked: employee_record_keeping_duties },
      { label: this.$tc("labels.reportingDuties"), checked: employee_reporting_duties },
    ];

    return rows;
  }

  get classes() {
    if (this.title === this.$root.$tc("labels.compliant"))
      return `${HOME_COMPLIANT_TITLE_CLASS}_compliant`;

    if (this.title === this.$root.$tc("labels.notCompliant"))
      return `${HOME_COMPLIANT_TITLE_CLASS}_noncompliant`;

    return `${HOME_COMPLIANT_TITLE_CLASS}_compliant_unknown`;
  }

  get cursorClass() {
    return {
      [`${COMPLIANCE_BLOCK_CLASS}`]: !this.isComplianceBlocked,
    };
  }

  onTasksClick() {
    if (this.isComplianceBlocked) return;

    this.$emit("click");
  }

  async goInviteEmployee() {
    if (this.isComplianceBlocked) return;

    const { data } = await this.axios.get(API_ROUTES.COMPANY_EMPLOYEE_ONBOARD_EMPLOYEE);

    const { task_id } = data;

    await this.$router.push(`${SMUser.basePath}${TASKS_PATH}${task_id}`);
  }
}
